/* RESPONSIBLE TEAM: team-standalone */
import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router';
import type IntlService from 'embercom/services/intl';
import { ResponseError } from 'embercom/lib/inbox/requests';
import type Transition from '@ember/routing/transition';
import type PlaygroundApiService from 'embercom/services/playground-api';
import { type GroupSummary } from 'embercom/lib/fin-playground/group-summary';
import { type PlaygroundQuestionGeneration } from 'embercom/lib/fin-playground/question-generation';

export default class TestingStandaloneRoute extends Route {
  @service declare appService: any;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare permissionsService: $TSFixMe;
  @service declare playgroundApi: PlaygroundApiService;

  analytics = {
    section: 'standalone',
    place: 'standalone.testing',
  };

  get titleToken() {
    return this.intl.t('standalone.navbar.test');
  }

  async beforeModel(transition: Transition) {
    try {
      await this.playgroundApi.validateAdminPermissions();
    } catch (err) {
      if (err instanceof ResponseError && err.response.status === 401) {
        // The permissions service will handle showing the modal
        // and redirecting when there is no from route
        await this.permissionsService.cancelTransitionAndShowPermissionModal(
          'can_access_all_conversations',
          transition,
          'apps.app.standalone.deploy.overview',
        );

        // If there is a from route we still want to transition to it
        // Otherwise we will stay on the current route with a loading spinner
        if (transition.from?.name) {
          this.router.transitionTo(transition.from.name);
        }

        return;
      }
      throw err;
    }
  }

  async model(): Promise<{
    groupSummaries: GroupSummary[];
    questionGenerationSummary: PlaygroundQuestionGeneration | null;
  }> {
    // Request the group summary
    await this.playgroundApi.reloadPlaygroundSummary();

    return {
      groupSummaries: this.playgroundApi.playgroundSummary?.groupSummaries ?? [],
      questionGenerationSummary:
        this.playgroundApi.playgroundSummary?.questionGenerationSummary ?? null,
    };
  }

  afterModel(resolvedModel: { groupSummaries: GroupSummary[] }): void {
    if (resolvedModel.groupSummaries.length > 0) {
      // If there are groups, transition to the test route using the first group id
      let firstGroupSummary = resolvedModel.groupSummaries[0];
      this.router.transitionTo('apps.app.standalone.testing.test', firstGroupSummary.id, {
        // We can skip the summary load and validation in the test
        // because we already did it in the index model
        queryParams: {
          skipSummaryLoad: true,
          skipAdminValidation: true,
        },
      });
    } else {
      // If there are no groups, remain on the index route
      return;
    }
  }
}
